import { RiskImpactChange } from '@/api/generated-catch-backend/model';
import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import Resources from '@/components/resources/resources';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CatchLabsCTA } from '@/app/components/common/CatchLabsCTA';
import { useListLabOrdersV1LabsOrdersGet } from '@/api/generated-catch-backend/laboratory-orders/laboratory-orders';
import DataCategoryModules, { DataCategoryModulesRef } from './components/health-data-categories/data-category-modules';
import { HealthDataEditorTitleDesktop } from './components/health-data-header/components/health-data-editor-title-desktop';
import { HealthDataEditorHeader } from './components/health-data-header/health-data-editor-header';
import { RecalculationInterstitial } from './components/recalculation-interstitial';
import { RiskImpactModal } from './components/risk-impact-modal/risk-impact-modal';
import { RiskImpactModalVariant } from './components/risk-impact-modal/risk-impact-modal.types';
import { RiskRecalculationBanner } from './components/risk-recalculation-banner';
import { useGetHealthData, useRecalculateRiskScore } from './use-health-data';

export default function HealthDataPage() {
  const dataCategoryModulesRef = useRef<DataCategoryModulesRef>(null);
  const location = useLocation();
  const [showRecalculationInterstitial, setShowRecalculationInterstitial] = useState(false);
  const [showRiskImpactModal, setShowRiskImpactModal] = useState(false);
  const [riskImpactData, setRiskImpactData] = useState<{
    variant: RiskImpactModalVariant;
    impacts: RiskImpactChange[];
  } | null>(null);

  // Get category name from navigation state
  const categoryName = location.state?.categoryName;

  const { isInitiallyFetching, mappedCategories, healthData } = useGetHealthData();
  const { recalculate, hasResponse } = useRecalculateRiskScore();
  const canRecalculateRiskScore = healthData?.metadata?.pendingChanges && healthData?.metadata?.isReadyForSubmission;

  // Fetch lab order status
  const { data: labOrdersData } = useListLabOrdersV1LabsOrdersGet();
  const labOrderStatus = labOrdersData?.meta.status;

  const handleRecalculate = async () => {
    if (!hasResponse) {
      console.warn('No questionnaire response found - user may need to complete the cancer risk assessment first');
      return;
    }

    // Show interstitial immediately
    setShowRecalculationInterstitial(true);

    recalculate({
      onSuccess: (data) => {
        setShowRecalculationInterstitial(false);

        if (data.hasRiskImpactChanges && data.riskImpactChanges && data.riskImpactChanges.length > 0) {
          const hasAnyDecrease = data.riskImpactChanges.some((impact) => impact.change < 0);
          const variant: RiskImpactModalVariant = hasAnyDecrease ? 'positive' : 'negative';

          setRiskImpactData({
            variant,
            impacts: data.riskImpactChanges,
          });
          setShowRiskImpactModal(true);
        }
      },
      onError: (error) => {
        console.error('Failed to recalculate risk score:', error);
        setShowRecalculationInterstitial(false);
      },
    });
  };

  // Scroll to category if one was passed via navigation state
  useEffect(() => {
    if (categoryName && dataCategoryModulesRef.current) {
      setTimeout(() => {
        dataCategoryModulesRef.current?.scrollToAndExpandCategory(categoryName);
      }, 300);
    }
  }, [categoryName]);

  // Map categories with a no-op onReviewClick (not used in edit mode)
  const categories = useMemo(
    () =>
      mappedCategories.map((category) => ({
        ...category,
        onReviewClick: () => {
          // No-op: in edit mode, expansion is handled by onToggleExpanded
        },
      })),
    [mappedCategories],
  );

  // Extract category names for navigation display
  const categoryNames = useMemo(() => categories.map((category) => category.name), [categories]);

  return (
    <>
      <DashboardNavigationBar activeItem="health-data" />
      <HealthDataEditorHeader
        titleContent={<HealthDataEditorTitleDesktop />}
        categories={categoryNames}
        categoryModulesRef={dataCategoryModulesRef}
      />

      <DashboardLayout>
        {canRecalculateRiskScore && (
          <div className="mb-6 lg:hidden">
            <RiskRecalculationBanner onRecalculate={handleRecalculate} />
          </div>
        )}

        <DashboardGrid>
          <DataCategoryModules
            ref={dataCategoryModulesRef}
            categories={categories}
            isEditMode={true}
            isInitiallyFetching={isInitiallyFetching}
            labOrderStatus={labOrderStatus}
          />
          <div className="flex flex-col gap-[24px]">
            {canRecalculateRiskScore && (
              <div className="hidden lg:block">
                <RiskRecalculationBanner onRecalculate={handleRecalculate} />
              </div>
            )}
            <CatchLabsCTA />
            <Resources as="aside" width="full" />
          </div>
        </DashboardGrid>
      </DashboardLayout>

      {/* Recalculation Interstitial Overlay */}
      {showRecalculationInterstitial && (
        <div className="fixed inset-0 z-50">
          <RecalculationInterstitial />
        </div>
      )}

      {/* Risk Impact Modal */}
      {riskImpactData && (
        <RiskImpactModal
          isOpen={showRiskImpactModal}
          onClose={() => setShowRiskImpactModal(false)}
          variant={riskImpactData.variant}
          impacts={riskImpactData.impacts}
        />
      )}
    </>
  );
}
